import { motion } from "framer-motion";
import { PRODUCT_ORDER, PRODUCTS } from "../lib/products";

const EcosystemDiagram = () => {
  // 6 products laid out around a center hub (clockwise from top)
  const positions = [
    { cx: 50, cy: 16 },   // top
    { cx: 82, cy: 32 },   // upper-right
    { cx: 82, cy: 64 },   // lower-right
    { cx: 50, cy: 78 },   // bottom
    { cx: 18, cy: 64 },   // lower-left
    { cx: 18, cy: 32 },   // upper-left
  ];

  return (
    <div className="relative mx-auto aspect-[5/4] w-full max-w-4xl">
      <svg
        viewBox="0 0 100 80"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(270, 100%, 60%)" stopOpacity="0.8" />
            <stop offset="100%" stopColor="hsl(270, 100%, 60%)" stopOpacity="0" />
          </radialGradient>
          {PRODUCT_ORDER.map((slug, i) => (
            <linearGradient
              id={`grad-${slug}`}
              key={slug}
              x1="50"
              y1="48"
              x2={positions[i].cx}
              y2={positions[i].cy}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="hsl(270, 100%, 60%)" stopOpacity="0.9" />
              <stop offset="100%" stopColor={PRODUCTS[slug].accentColor} stopOpacity="0.8" />
            </linearGradient>
          ))}
        </defs>

        {/* hub glow */}
        <circle cx="50" cy="48" r="18" fill="url(#hub-glow)" />

        {/* connecting lines */}
        {PRODUCT_ORDER.map((slug, i) => {
          const p = positions[i];
          // pull the line back ~5 viewBox units before the node center to leave a visible gap
          const dx = p.cx - 50;
          const dy = p.cy - 48;
          const len = Math.sqrt(dx * dx + dy * dy);
          const gap = 9;
          const x2 = p.cx - (dx / len) * gap;
          const y2 = p.cy - (dy / len) * gap;
          return (
            <motion.line
              key={slug}
              x1={50}
              y1={48}
              x2={x2}
              y2={y2}
              stroke={`url(#grad-${slug})`}
              strokeWidth="0.35"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.2 + i * 0.12, ease: "easeOut" }}
            />
          );
        })}

        {/* center hub */}
        <circle cx="50" cy="48" r="6" fill="hsl(222, 47%, 6%)" stroke="hsl(270, 100%, 60%)" strokeWidth="0.4" />
        <text
          x="50"
          y="49.6"
          textAnchor="middle"
          fontSize="2.6"
          fontWeight="800"
          fill="white"
          fontFamily="Manrope, sans-serif"
          letterSpacing="0.2"
        >
          TRYB
        </text>
      </svg>

      {/* product nodes */}
      {PRODUCT_ORDER.map((slug, i) => {
        const p = positions[i];
        const product = PRODUCTS[slug];
        const Icon = product.icon;
        return (
          <motion.div
            key={slug}
            initial={{ opacity: 0, scale: 0.6, x: "-50%", y: "-50%" }}
            whileInView={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 + i * 0.12 }}
            className="absolute"
            style={{
              left: `${p.cx}%`,
              top: `${(p.cy / 80) * 100}%`,
            }}
            data-testid={`ecosystem-node-${slug}`}
          >
            <div
              className="flex flex-col items-center gap-2"
              style={{ filter: `drop-shadow(0 10px 30px ${product.accentGlow})` }}
            >
              <div
                className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-2xl border"
                style={{
                  background: product.accentColor,
                  borderColor: product.accentColor,
                  boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.18), inset 0 -12px 24px -8px rgba(0,0,0,0.25), 0 8px 22px -10px ${product.accentGlow}`,
                }}
              >
                <Icon className="h-7 w-7 text-white" strokeWidth={1.8} />
              </div>
              <span className="text-[11px] md:text-xs font-semibold text-foreground/90 whitespace-nowrap">
                {product.name}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default EcosystemDiagram;
